import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield, Heart, Users, Building2, Star, ChevronRight, Zap, TrendingUp } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import PublicHeader from "@/components/shared/PublicHeader";
import Footer from "@/components/Footer";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AboutUsPage() {
    const { lang } = useI18n();

    // Total top-level specialties in the catalog (independent of active offers),
    // pulled live from the homepage endpoint's stats. Falls back to 12.
    const [specialtyCount, setSpecialtyCount] = useState(null);

    useEffect(() => {
        fetch(`${API}/api/public/homepage`)
            .then((r) => r.json())
            .then((json) => {
                if (json?.stats?.total_specialties != null) setSpecialtyCount(json.stats.total_specialties);
            })
            .catch(() => { /* keep fallback */ });
    }, []);

    const nSpecialties = specialtyCount ?? 12;
    const specialtiesEn = `${nSpecialties}+`;
    const specialtiesAr = `+${nSpecialties}`;

    const stats = [
        { value: "500+",        label: lang === "ar" ? "مقدم خدمة صحية"  : "Healthcare Providers" },
        { value: "50,000+",     label: lang === "ar" ? "مستفيد"           : "Members Served" },
        { value: "40%",         label: lang === "ar" ? "متوسط التوفير"    : "Average Savings" },
        { value: specialtiesEn, label: lang === "ar" ? "تخصص طبي"         : "Medical Specialties" },
    ];

    const values = [
        {
            Icon: Shield,
            title: lang === "ar" ? "الوصول حق غير قابل للتفاوض" : "Access Is Non-Negotiable",
            body: lang === "ar"
                ? "نرفض فكرة أن الرعاية الصحية رفاهية. كل ما نبنيه يهدف إلى جعلها أكثر سهولة وتوفراً للجميع."
                : "We reject the idea that quality healthcare is a luxury. Every feature we build is driven by one question: does this make healthcare more accessible?",
        },
        {
            Icon: Star,
            title: lang === "ar" ? "الشفافية تبني الثقة" : "Transparency Builds Trust",
            body: lang === "ar"
                ? "لا رسوم مخفية، لا مفاجآت. كل سعر واضح قبل موعدك — ما تراه هو ما تدفعه."
                : "Hidden fees and unclear billing are the enemy of patient confidence. On Hemaia, every price is visible before the appointment. No small print. No surprises.",
        },
        {
            Icon: Heart,
            title: lang === "ar" ? "الجودة دون تنازل" : "Quality Without Compromise",
            body: lang === "ar"
                ? "التوفير لا يعني التنازل عن الجودة. جميع مقدمي الخدمة معتمدون ويخضعون للتدقيق قبل الانضمام."
                : "Savings should never mean settling. Every provider in our network is vetted for licensing and patient satisfaction. We negotiate on price — never on standard of care.",
        },
        {
            Icon: Zap,
            title: lang === "ar" ? "التكنولوجيا لخدمة الإنسان" : "Technology in Service of People",
            body: lang === "ar"
                ? "نصمم منصتنا لتكون سهلة للجميع — من كبار السن الذين يحجزون أول زيارة متخصصة، إلى المستخدمين المتكررين."
                : "Our platform is built to be intuitive for a 60-year-old booking their first specialist visit, and fast for a 28-year-old booking their third in a month.",
        },
        {
            Icon: Users,
            title: lang === "ar" ? "نموّنا مرتبط بنمو مجتمعنا" : "We Grow When Our Communities Grow",
            body: lang === "ar"
                ? "نجاحنا لا يقاس بالإيرادات وحدها، بل بتحسين الوصول للخدمات الصحية وخفض التكاليف على مجتمعاتنا."
                : "Hemaia's success is measured not in revenue alone, but in health outcomes improved, costs reduced, and appointments kept.",
        },
        {
            Icon: TrendingUp,
            title: lang === "ar" ? "بناء للمستقبل الطويل" : "Built for the Long Term",
            body: lang === "ar"
                ? "التزامنا للأعضاء واضح: أفضل الأسعار، أعلى جودة، وشفافية كاملة — دائماً."
                : "Our commitment to members is clear: we will always negotiate on your behalf, maintain the highest provider standards, and keep our pricing honest.",
        },
    ];

    const specialties = [
        { en: "Dental",              ar: "الأسنان" },
        { en: "Doctor Consultation", ar: "الاستشارات الطبية" },
        { en: "Lab Tests",           ar: "التحاليل" },
        { en: "Scans & Diagnostics", ar: "الأشعة والتشخيص" },
        { en: "Skin & Beauty",       ar: "الجلدية والتجميل" },
        { en: "Eye & Vision",        ar: "العيون" },
        { en: "Physiotherapy",       ar: "العلاج الطبيعي" },
        { en: "Mental Health",       ar: "الصحة النفسية" },
        { en: "Maternity",           ar: "الأمومة" },
        { en: "Weight Management",   ar: "إدارة الوزن" },
        { en: "Home Care",           ar: "الرعاية المنزلية" },
        { en: "Alternative Medicine",ar: "الطب البديل" },
    ];

    const steps = [
        {
            num: "01",
            title: lang === "ar" ? "انضم إلى حماية" : "Join Hemaia",
            body: lang === "ar"
                ? "سجّل خلال أقل من 3 دقائق واختر الخطة المناسبة (فردي، زوجي، عائلي). لا يوجد فحص طبي. التغطية تبدأ فوراً."
                : "Sign up online in under 3 minutes. No medical history required. No waiting period. Coverage begins immediately.",
        },
        {
            num: "02",
            title: lang === "ar" ? "اختر مقدم الخدمة" : "Find Your Provider",
            body: lang === "ar"
                ? "تصفح أكثر من 500 مستشفى وعيادة حسب التخصص أو الموقع. اقرأ التقييمات وقارن الأسعار."
                : "Browse our network of 500+ hospitals and clinics by specialty, location, or provider name. Read verified reviews and compare pricing.",
        },
        {
            num: "03",
            title: lang === "ar" ? "احجز موعدك" : "Book Your Appointment",
            body: lang === "ar"
                ? "احجز مباشرة عبر المنصة. بيانات عضويتك تُشارك تلقائياً — العيادة ستعرف سعرك."
                : "Book directly through the Hemaia platform. Your membership details are shared automatically — the clinic knows your rate.",
        },
        {
            num: "04",
            title: lang === "ar" ? "اعرض عضويتك وخلاص" : "Simply Present Your Membership",
            body: lang === "ar"
                ? "احضر موعدك، أبرز عضويتك، وادفع السعر المخفض مباشرة. بدون مطالبات. بدون انتظار."
                : "Arrive at your appointment, present your Hemaia membership, and pay the discounted rate directly. No claims. No reimbursements.",
        },
    ];

    const team = [
        { name: "Dr Adham Ismail",  role: lang === "ar" ? "شريك مؤسس والرئيس التنفيذي"         : "Co-Founder & CEO",                   detail: lang === "ar" ? "الرؤية، الاستراتيجية، الشراكات"    : "Vision, Strategy & Partnerships" },
        { name: "Ali Khalid",       role: lang === "ar" ? "شريك مؤسس — رئيس التوسع"             : "Co-Founder, Chief Scaling Officer",   detail: lang === "ar" ? "بناء، نمو وتوسع"                    : "Build, Growth & Scaling" },
        { name: "Dr Sherif Ismail", role: lang === "ar" ? "شريك مؤسس — تطوير الأعمال"           : "Co-Founder, Business Development",    detail: lang === "ar" ? "الشراكات المؤسسية والشركات"         : "Corporate & B2B Partnerships" },
        { name: "Rami Taher",       role: lang === "ar" ? "شريك مؤسس — رئيس المنتج والتقنية"    : "Co-Founder, CPO & CTO",               detail: lang === "ar" ? "تطوير المنصة والمنتجات"             : "Platform & Product Development" },
    ];

    return (
        <div className="min-h-screen bg-background" data-testid="about-us-page">
            <PublicHeader />

            <main className="pt-16">
                {/* Hero — full-width image overlay */}
                <section className="relative overflow-hidden py-24 sm:py-32">
                    <div className="absolute inset-0">
                        <img
                            src="https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=1600"
                            alt=""
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/85 via-primary/75 to-primary/60" />
                    </div>
                    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center text-white">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70 mb-4">
                            {lang === "ar" ? "قصتنا" : "Our Story"}
                        </p>
                        <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-6 leading-[1.1]">
                            {lang === "ar"
                                ? <>الرعاية الصحية لازم تكون<br /><span className="text-white/90">في متناول إيدك</span></>
                                : <>Healthcare Should Feel<br /><span className="text-white/90">Within Reach</span></>}
                        </h1>
                        <p className="text-lg text-white/85 max-w-2xl mx-auto leading-relaxed">
                            {lang === "ar"
                                ? "اتأسست حماية على فكرة بسيطة: محدش المفروض يكون مضطر يختار بين الاطمئنان المادي والوصول للرعاية الصحية. إحنا بنينا تجربة عضوية بتقرّب لك الرعاية الصحية الموثوقة، بكل وضوح وثقة."
                                : "Hemaia was founded on a simple belief: no one should have to choose between financial peace of mind and access to care. We built a membership experience that brings trusted healthcare closer, with clarity and confidence."}
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
                            <Link to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-semibold text-primary hover:opacity-90 transition-opacity shadow-md">
                                {lang === "ar" ? "انضم الآن" : "Join Now"}
                                <ChevronRight className="h-4 w-4" />
                            </Link>
                            <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl border border-white/40 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors">
                                {lang === "ar" ? "تواصل معنا" : "Contact Us"}
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Stats */}
                <section className="py-16 border-y border-border/50 bg-white">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
                            {stats.map(({ value, label }) => (
                                <div key={label} className="text-center">
                                    <div className="text-4xl font-black text-primary mb-1">{value}</div>
                                    <div className="text-sm text-muted-foreground font-medium">{label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Why Hemaia exists */}
                <section className="py-16 sm:py-20">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6">
                        <div className="grid md:grid-cols-2 gap-12 items-start">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">
                                    {lang === "ar" ? "لماذا وجدت حماية" : "Why Hemaia Exists"}
                                </p>
                                <h2 className="text-3xl font-bold text-foreground mb-5">
                                    {lang === "ar"
                                        ? "أزمة القدرة على تحمل تكاليف الرعاية الصحية"
                                        : "The Healthcare Affordability Crisis"}
                                </h2>
                                <div className="space-y-4 text-muted-foreground leading-relaxed">
                                    <p>
                                        {lang === "ar"
                                            ? "في مصر ومنطقة الشرق الأوسط وشمال إفريقيا، يواجه ملايين الأشخاص مفارقة صامتة: مرافق صحية عالمية المستوى متوفرة على مقربة منهم، ومع ذلك يؤجل الكثيرون أو يتجنبون العلاج بسبب التكلفة."
                                            : "Across Egypt and the wider MENA region, millions of people face a silent paradox — world-class healthcare facilities exist within driving distance, yet a large portion of the population delays or avoids medical care because of cost."}
                                    </p>
                                    <p>
                                        {lang === "ar"
                                            ? "الكثيرون لا يمتلكون تأمينا صحيا من جهة عمل. البعض يعمل بشكل مستقل أو في قطاعات لا توفر التأمين الطبي. وحتى من لديهم تأمين، غالباً ما لا يغطي الخدمات الأكثر احتياجاً — طب الأسنان، الاستشارات التخصصية، الأشعة، الصحة النفسية."
                                            : "Many are people without employer-sponsored coverage — self-employed, between jobs, or working in sectors where benefits simply don't reach. Others have insurance, but their policies exclude the very treatments they need most: dental, specialist consultations, diagnostic imaging, mental health support."}
                                    </p>
                                    <p className="font-medium text-foreground">
                                        {lang === "ar"
                                            ? "نظرنا إلى هذا الواقع ورفضنا اعتباره أمراً طبيعياً. حماية هي الحل."
                                            : "We looked at this reality and refused to accept it as inevitable. Hemaia is our answer."}
                                    </p>
                                </div>
                            </div>
                            <div className="bg-primary/5 rounded-2xl p-8">
                                <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-4">
                                    {lang === "ar" ? "ماذا نقدم" : "What We Do"}
                                </p>
                                <h3 className="text-xl font-bold text-foreground mb-4">
                                    {lang === "ar" ? "شبكة رعاية صحية اتصممت عشانك" : "A Healthcare Network Designed Around You"}
                                </h3>
                                <p className="text-muted-foreground leading-relaxed mb-6">
                                    {lang === "ar"
                                        ? "حماية منصة رقمية تربط الأعضاء بشبكة موثقة من المستشفيات والعيادات ومقدمي الخدمات المتخصصين — مع خصومات حقيقية وموثقة. نحن لسنا شركة تأمين. ما نقدمه أبسط وأسرع وأكثر صدقاً."
                                        : "Hemaia is a digital health platform that connects members with a curated network of hospitals, clinics, and specialist providers — all offering real, verified discounts. We are not an insurance company. What we offer is simpler, faster, and more honest."}
                                </p>
                                <blockquote className="border-s-4 border-primary ps-4 italic text-foreground text-sm leading-relaxed">
                                    {lang === "ar"
                                        ? '"لم نبنِ حماية لتغيير قطاع الرعاية الصحية. بنيناها لأن هذا القطاع كان يعطل حياة الناس — وكان لا بد أن يتغير."'
                                        : '"We didn\'t build Hemaia to disrupt healthcare. We built it because healthcare was disrupting people\'s lives — and that had to change."'}
                                    <footer className="mt-2 not-italic font-semibold text-primary text-xs">
                                        — {lang === "ar" ? "د. أدهم إسماعيل، المؤسس والرئيس التنفيذي" : "Dr Adham Ismail, Co-Founder & CEO"}
                                    </footer>
                                </blockquote>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Specialties */}
                <section className="py-16 bg-white">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6">
                        <div className="text-center mb-10">
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">
                                {lang === "ar" ? "التغطية" : "What We Cover"}
                            </p>
                            <h2 className="text-3xl font-bold text-foreground">
                                {lang === "ar" ? `${specialtiesAr} تخصص. عضوية واحدة. كل الميزات، متضمنة.` : `${specialtiesEn} Specialties. One Membership. Every Benefit, Included.`}
                            </h2>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                            {specialties.map(({ en, ar }) => (
                                <div key={en} className="flex items-center gap-2.5 rounded-xl border border-border/50 bg-background px-4 py-3">
                                    <div className="h-2 w-2 rounded-full bg-primary shrink-0" />
                                    <span className="text-sm font-medium text-foreground">{lang === "ar" ? ar : en}</span>
                                </div>
                            ))}
                        </div>
                        <p className="text-center text-sm text-muted-foreground mt-6">
                            {lang === "ar"
                                ? "كعضو في حماية، بتستفيد من أسعار مميزة في شبكتنا كلها، بتوفر لك عادةً من 20٪ إلى 60٪ مقارنةً بأسعار السوق العادية."
                                : "As a Hemaia member, you access preferred pricing across our entire network, typically 20–60% below standard market rates."}
                        </p>
                    </div>
                </section>

                {/* How It Works */}
                <section className="py-16 sm:py-20">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6">
                        <div className="text-center mb-12">
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">
                                {lang === "ar" ? "خطوات بسيطة" : "How It Works"}
                            </p>
                            <h2 className="text-3xl font-bold text-foreground">
                                {lang === "ar" ? "سهل. فوري. قيمة استثنائية." : "Effortless. Immediate. Exceptional Value."}
                            </h2>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {steps.map(({ num, title, body }) => (
                                <div key={num} className="relative p-6 rounded-2xl border border-border/50 bg-white">
                                    <span className="text-5xl font-black text-primary/10 select-none absolute top-3 end-4">{num}</span>
                                    <p className="text-primary font-bold text-sm mb-2">{num}</p>
                                    <h3 className="text-base font-semibold text-foreground mb-2">{title}</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Values */}
                <section className="py-16 sm:py-20 bg-muted/30">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6">
                        <div className="text-center mb-12">
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">
                                {lang === "ar" ? "قيمنا" : "Our Values"}
                            </p>
                            <h2 className="text-3xl font-bold text-foreground">
                                {lang === "ar" ? "المبادئ التي نبني عليها كل شيء" : "The Principles We Build Everything On"}
                            </h2>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {values.map(({ Icon, title, body }) => (
                                <div key={title} className="bg-white rounded-2xl p-6 border border-border/50">
                                    <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                                        <Icon className="h-5 w-5 text-primary" />
                                    </div>
                                    <h3 className="text-base font-semibold text-foreground mb-2">{title}</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Team */}
                <section className="py-16 sm:py-20 bg-white">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6">
                        <div className="text-center mb-12">
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">
                                {lang === "ar" ? "الفريق" : "Meet the Team"}
                            </p>
                            <h2 className="text-3xl font-bold text-foreground">
                                {lang === "ar" ? "بُنيت بواسطة أشخاص يؤمنون بالصحة للجميع" : "Built by People Who Care Deeply About Healthcare"}
                            </h2>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {team.map(({ name, role, detail }) => (
                                <div key={name} className="rounded-2xl border border-border/50 p-6 text-center">
                                    <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                        <span className="text-xl font-black text-primary">
                                            {name.split(" ").filter(w => !w.startsWith("Dr")).map(w => w[0]).slice(0, 2).join("")}
                                        </span>
                                    </div>
                                    <h3 className="text-sm font-bold text-foreground mb-1">{name}</h3>
                                    <p className="text-xs font-semibold text-primary mb-1">{role}</p>
                                    <p className="text-xs text-muted-foreground">{detail}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="py-16 sm:py-20 bg-primary">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
                        <h2 className="text-3xl font-bold text-white mb-4">
                            {lang === "ar" ? "سواء كنت مريضاً أو مقدم خدمة — حماية مصممة لك" : "Whether You're a Patient or a Provider — Hemaia Is Built for You"}
                        </h2>
                        <p className="text-white/80 mb-8 leading-relaxed">
                            {lang === "ar"
                                ? "إذا كنت تبحث عن التحكم في تكاليفك الصحية، فعضوية حماية هي أفضل استثمار في صحتك هذا العام."
                                : "If you're looking to take control of your healthcare costs, Hemaia membership is the smartest investment in your health you'll make this year."}
                        </p>
                        <div className="flex flex-wrap gap-4 justify-center">
                            <Link to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-white text-primary px-6 py-3 text-sm font-semibold hover:bg-white/90 transition-colors">
                                {lang === "ar" ? "انضم كعضو" : "Become a Member"}
                            </Link>
                            <Link to="/for-providers" className="inline-flex items-center gap-2 rounded-xl border border-white/40 text-white px-6 py-3 text-sm font-semibold hover:bg-white/10 transition-colors">
                                {lang === "ar" ? "انضم كمقدم خدمة" : "Join as a Provider"}
                            </Link>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
